import axios from './axiosInstance';
import axiosLib from 'axios';
import type {
  NotificationConfig,
  NotificationEventType,
  NotificationRecipientType,
  NotificationConfigType,
  NotificationConditionConfiguration,
  FormNotificationEventType,
} from '../../types';

interface SportArea {
  sport: string;
  areaId: string;
}

const cancelTokenFor = (abortSignal?: AbortSignal) => {
  const source = axiosLib.CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => source.cancel('Operation canceled by the user.'));
  }
  return source.token;
};

interface GetNotificationConfigsParams extends SportArea {
  scopeType?: string;
  scopeId?: string | null;
  // CSV-matched: pass an array; the handler matches ANY of the targetIds.
  targetId?: string | string[];
  recipientType?: NotificationRecipientType;
  recipientId?: string | string[];
  type?: NotificationConfigType;
  abortSignal?: AbortSignal;
}
const getNotificationConfigs = ({
  sport,
  areaId,
  scopeType,
  scopeId,
  targetId,
  recipientType,
  recipientId,
  type,
  abortSignal,
}: GetNotificationConfigsParams): Promise<NotificationConfig[]> => {
  const params: Record<string, string> = {};
  if (scopeType) {
    params.scopeType = scopeType;
  }
  if (scopeId) {
    params.scopeId = scopeId;
  }
  if (targetId) {
    params.targetId = Array.isArray(targetId) ? targetId.join(',') : targetId;
  }
  if (recipientType) {
    params.recipientType = recipientType;
  }
  if (recipientId) {
    params.recipientId = Array.isArray(recipientId) ? recipientId.join(',') : recipientId;
  }
  if (type) {
    params.type = type;
  }
  return axios
    .get(`/${sport}/areas/${areaId}/notifications/configs`, {
      params,
      cancelToken: cancelTokenFor(abortSignal),
    })
    .then((r) => (r.data && r.data.configs) || [])
    .catch((e) => {
      console.log(e);
      throw e;
    });
};

export interface CreateNotificationConfigBody {
  type: NotificationConfigType;
  scopeType: string;
  scopeId?: string | null;
  targetId?: string | null;
  eventType: NotificationEventType;
  offsetMinutes?: number | null;
  recipientType: NotificationRecipientType;
  recipientId?: string | null;
  enabled?: boolean;
}
interface CreateNotificationConfigParams extends SportArea {
  config: CreateNotificationConfigBody;
}
// PUT = create.
const createNotificationConfig = ({
  sport,
  areaId,
  config,
}: CreateNotificationConfigParams): Promise<NotificationConfig> =>
  axios
    .put(`/${sport}/areas/${areaId}/notifications/configs`, config)
    .then((r) => r.data.config)
    .catch((e) => {
      console.log(e);
      throw e;
    });

export interface UpdateNotificationConfigBody {
  eventType?: NotificationEventType;
  offsetMinutes?: number | null;
  recipientType?: NotificationRecipientType;
  recipientId?: string | null;
  targetId?: string | null;
  enabled?: boolean;
}
interface UpdateNotificationConfigParams extends SportArea {
  configId: string;
  config: UpdateNotificationConfigBody;
}
// POST = update.
const updateNotificationConfig = ({
  sport,
  areaId,
  configId,
  config,
}: UpdateNotificationConfigParams): Promise<NotificationConfig> =>
  axios
    .post(`/${sport}/areas/${areaId}/notifications/configs/${configId}`, config)
    .then((r) => r.data.config)
    .catch((e) => {
      console.log(e);
      throw e;
    });

interface DeleteNotificationConfigParams extends SportArea {
  configId: string;
}
const deleteNotificationConfig = ({
  sport,
  areaId,
  configId,
}: DeleteNotificationConfigParams): Promise<{ success: boolean }> =>
  axios
    .delete(`/${sport}/areas/${areaId}/notifications/configs/${configId}`)
    .then((r) => r.data)
    .catch((e) => {
      console.log(e);
      throw e;
    });

// ── Form notification rules (ULLR-222, API v22) ────────────────────────────
// A rule is one row per recipient sharing a `ruleId`; the rule is the unit:
// create writes every row, an update (via any member row id) re-syncs the whole
// rule, a delete removes it. Contract: UllrServerless
// docs/specs/ullr-222/v22-contract.md. Never send `enabled` (rules are always
// on; a person mutes form notifications in their own settings).

export interface FormRuleRecipient {
  recipientType: 'user' | 'user_group';
  recipientId: string;
}

export interface CreateFormRuleBody {
  type: 'custom';
  scopeType: 'form';
  scopeId: string;
  eventType: FormNotificationEventType;
  recipients: FormRuleRecipient[];
  condition: NotificationConditionConfiguration | null;
  messageTemplate: string | null;
}
interface CreateFormRuleParams extends SportArea {
  rule: CreateFormRuleBody;
}
export interface FormRuleWriteResult {
  /** The rule's rows (one per recipient), oldest first. */
  configs: NotificationConfig[];
  /** Move only: condition references the target form could not resolve. */
  warnings: string[];
}
const createFormRule = ({ sport, areaId, rule }: CreateFormRuleParams): Promise<FormRuleWriteResult> =>
  axios
    .put(`/${sport}/areas/${areaId}/notifications/configs`, rule)
    .then((r) => ({ configs: (r.data && r.data.configs) || [], warnings: [] }))
    .catch((e) => {
      console.log(e);
      throw e;
    });

export interface UpdateFormRuleBody {
  recipients?: FormRuleRecipient[];
  condition?: NotificationConditionConfiguration | null;
  messageTemplate?: string | null;
  /** Move a rule from a built-in (`DEFAULT_*`) form to a saved copy. */
  scopeId?: string;
}
interface UpdateFormRuleParams extends SportArea {
  /** Any row of the rule. */
  configId: string;
  rule: UpdateFormRuleBody;
}
const updateFormRule = ({ sport, areaId, configId, rule }: UpdateFormRuleParams): Promise<FormRuleWriteResult> =>
  axios
    .post(`/${sport}/areas/${areaId}/notifications/configs/${configId}`, rule)
    .then((r) => ({
      configs: (r.data && r.data.configs) || [],
      warnings: (r.data && Array.isArray(r.data.warnings)) ? r.data.warnings : [],
    }))
    .catch((e) => {
      console.log(e);
      throw e;
    });

interface DeleteFormRuleParams extends SportArea {
  /** Any row of the rule. */
  configId: string;
}
const deleteFormRule = ({ sport, areaId, configId }: DeleteFormRuleParams): Promise<{ deletedConfigIds: string[] }> =>
  axios
    .delete(`/${sport}/areas/${areaId}/notifications/configs/${configId}`)
    .then((r) => ({ deletedConfigIds: (r.data && r.data.deletedConfigIds) || [] }))
    .catch((e) => {
      console.log(e);
      throw e;
    });

const notificationConfigFunctions = {
  getNotificationConfigs,
  createNotificationConfig,
  updateNotificationConfig,
  deleteNotificationConfig,
  createFormRule,
  updateFormRule,
  deleteFormRule,
};

export default notificationConfigFunctions;
